import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Fieldset, Hourglass, NumberInput, Panel, Radio, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { Children, ChildrenNames, Ethnicity, EthnicityNames, FamilyPlan, FamilyPlanNames, Frequency, FrequencyNames, Intention, IntentionNames, RelationshipType, RelationshipTypeNames } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 40,
    alignItems: "center",
    alignSelf: "center",
    flexWrap: "wrap",
  },
});

export default function CigarettesScreen() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const [frequency, setFrequency] = useState(communityMembership.profile.smoke ?? 0);

  const onSubmit = useCallback(
    async (skip: boolean) => {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "CigarettesScreen.onSubmit",
          "/rabbl/api/optionalsettings",
          "POST",
          {
            domain: domain,
            smoke: 1,
            frequency: frequency,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          router.back();
        } else {
          router.navigate(AppUtils.getProfileCreateNextOptionalPageLink("cigarettes", membership) as RelativePathString);
        }

      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(skip), true);
        AppUtilities.handleException(e, "CigarettesScreen.onSubmit");
      } finally {
        hideSpinner();
      }
    },
    [
      router,
      domain,
      frequency,
    ]
  );

  return (
    <BasicScrollView center={true}>
      <Meta title={"Cigarettes"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>Do you smoke cigarettes?</AppText>

          <Radio
            status={frequency == Frequency.FREQUENCY_NONE ? 'checked' : 'unchecked'}
            onPress={() => setFrequency(Frequency.FREQUENCY_NONE)}
            label={FrequencyNames.get(Frequency.FREQUENCY_NONE)}
          />

          <Radio
            status={frequency == Frequency.FREQUENCY_NO ? 'checked' : 'unchecked'}
            onPress={() => setFrequency(Frequency.FREQUENCY_NO)}
            label={FrequencyNames.get(Frequency.FREQUENCY_NO)}
          />

          <Radio
            status={frequency == Frequency.FREQUENCY_SOMETIMES ? 'checked' : 'unchecked'}
            onPress={() => setFrequency(Frequency.FREQUENCY_SOMETIMES)}
            label={FrequencyNames.get(Frequency.FREQUENCY_SOMETIMES)}
          />

          <Radio
            status={frequency == Frequency.FREQUENCY_YES ? 'checked' : 'unchecked'}
            onPress={() => setFrequency(Frequency.FREQUENCY_YES)}
            label={FrequencyNames.get(Frequency.FREQUENCY_YES)}
          />

          <View style={styles.flexRow}>
            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(false)}
              >{ edit ? "Save" : "Next" }</Button95>
            </View>

            {
              (!edit || edit.length == 0) && (
                <>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => onSubmit(true)}
                    >Skip</Button95>
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => router.navigate(AppUtils.getProfileCreateNextPageLink(communityMembership) as RelativePathString)}
                    >Skip all</Button95>
                  </View>
                </>
              )
            }
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
